<section
  #workspace
  class="page-editor"
  aria-label="Editable notice page"
  (scroll)="rememberViewState()"
  (pointerdown)="dismissInsertionOutside($event)"
  tabindex="-1"
>
  <div #pageToolbar class="page-toolbar">
    <div class="page-tools" aria-label="Page editing actions">
      <button
        uiButton="secondary"
        type="button"
        (click)="historyCommand.emit('undo')"
        [disabled]="disabled || !canUndo"
        aria-keyshortcuts="Control+Z Meta+Z"
      >
        Undo
      </button>
      <button
        uiButton="secondary"
        type="button"
        (click)="historyCommand.emit('redo')"
        [disabled]="disabled || !canRedo"
        aria-keyshortcuts="Control+Shift+Z Meta+Shift+Z Control+Y"
      >
        Redo
      </button>
      <button
        #tokenTrigger
        uiButton="secondary"
        type="button"
        (click)="openTokenPicker()"
        [disabled]="!editable || !selected?.runs"
        [attr.aria-expanded]="tokenPickerOpen"
      >
        Insert token
      </button>
      <button
        #blockTrigger
        uiButton="secondary"
        type="button"
        (click)="openBlockPicker()"
        [disabled]="!canAddBlock"
        [attr.aria-expanded]="blockPickerOpen"
      >
        Insert block
      </button>
      <button
        uiButton="secondary"
        type="button"
        (click)="toggleOutline()"
        [attr.aria-expanded]="outlineOpen"
      >
        Outline
      </button>
    </div>
    <div class="page-tools">
      <label
        >Zoom<select
          uiControl
          name="page-editor-zoom"
          [ngModel]="zoom"
          (ngModelChange)="changeZoom($event)"
        >
          <option [ngValue]="0.5">50%</option>
          <option [ngValue]="0.75">75%</option>
          <option [ngValue]="1">100%</option>
        </select></label
      >
      <button
        #pageSetupTrigger
        uiButton="secondary"
        type="button"
        (click)="openDocumentSettings('size', $event)"
        [attr.aria-pressed]="propertiesOpen && inspectorMode === 'document'"
      >
        Page setup
      </button>
      <button
        #propertiesTrigger
        uiButton="secondary"
        type="button"
        (click)="toggleProperties()"
        [attr.aria-expanded]="propertiesOpen"
      >
        Properties
      </button>
    </div>
  </div>
  @if (structureMessage) {
    <p
      class="structure-message"
      [attr.role]="structureError ? 'alert' : 'status'"
    >
      {{ structureMessage }}
    </p>
  }
  @if (referenceError) {
    <p role="alert">{{ referenceError }}</p>
  }
  @if (settingsError) {
    <p role="alert">{{ settingsError }}</p>
  }
  @if (unavailableBranding) {
    <p role="alert">
      {{
        composition.branding.profile
          ? brandError ||
            'The retained brand profile is unavailable. Inspect Fields to refresh or remove it; your source is preserved.'
          : 'A retained logo or watermark is unavailable in this contract. Its identity is preserved; choose a supported asset in Page setup or inspect Fields.'
      }}
    </p>
  }
  @if (insertError()) {
    <p role="alert">{{ insertError() }}</p>
  }
  @if (fontError()) {
    <p role="alert">
      {{ fontError() }}
      <button
        uiButton="secondary"
        type="button"
        (click)="fieldsRequested.emit()"
      >
        Open Fields
      </button>
    </p>
  } @else if (!fontReady()) {
    <p role="status">Loading the document font…</p>
  }
  <div
    #pageLayout
    class="page-layout"
    [class.properties-closed]="!propertiesOpen"
    [class.outline-open]="outlineOpen"
  >
    @if (insertionMode; as mode) {
      @let bounds = insertionPanelBounds;
      <phms-notice-insertion-panel
        #insertionPanel
        class="page-insertion-panel"
        [style.top.px]="bounds?.top"
        [style.height.px]="bounds?.height"
        [style.max-height.px]="bounds?.height"
        [mode]="mode"
        [disabled]="!editable"
        [attr.aria-busy]="insertingToken"
        [tokenKind]="tokenKind"
        [tokenCode]="tokenCode"
        [tokenFormat]="tokenFormat"
        [tokenSearch]="tokenSearch"
        [tokenOptions]="filteredTokenOptions"
        [tokenFormats]="tokenFormats"
        [insertionKind]="insertionKind"
        [insertionPosition]="insertionPosition"
        [canAddBlock]="canAddBlock"
        [canAddTable]="canAddTable"
        [blocksUsed]="composition.blocks.length"
        [blockLimit]="catalog.limits.blocks"
        [selectedLabel]="selected ? label(selected) : ''"
        (tokenKindChange)="chooseTokenKind($event)"
        (tokenCodeChange)="chooseToken($event)"
        (tokenFormatChange)="tokenFormat = $event"
        (tokenSearchChange)="searchTokens($event)"
        (insertionKindChange)="insertionKind = $event"
        (insertionPositionChange)="insertionPosition = $event"
        (insertToken)="insertToken()"
        (insertBlock)="insertBlock()"
        (closed)="closeInsertionPicker()"
      />
    }
    @if (outlineOpen) {
      <nav
        #outlinePane
        class="document-outline"
        aria-label="Document reading order"
        (scroll)="rememberViewState()"
      >
        <h3>Reading order</h3>
        <ol>
          @for (block of composition.blocks; track block.id) {
            <li>
              <button
                uiButton="secondary"
                type="button"
                [attr.aria-label]="'Select ' + label(block)"
                [attr.aria-pressed]="selected?.id === block.id"
                (click)="selectOutline(block)"
                [disabled]="disabled"
              >
                <strong>{{ label(block) }}</strong
                ><small>{{ outlineText(block) }}</small>
              </button>
            </li>
          }
        </ol>
      </nav>
    }
    <div
      #canvas
      class="page-canvas"
      aria-label="Notice composition"
      [attr.aria-busy]="!fontReady()"
      (scroll)="rememberViewState()"
    >
      <article
        class="document-paper"
        [ngStyle]="paperStyle"
        aria-label="Notice page composition"
      >
        @if (showWatermark) {
          <div
            class="document-watermark"
            aria-hidden="true"
            [ngStyle]="watermarkStyle"
          >
            {{ watermarkText }}
          </div>
        }
        @if (composition.branding.profile && showLogo) {
          <div
            class="document-brand-logo"
            [style.justify-content]="logoAlignment"
          >
            <img
              [src]="brandLogoUrl"
              [alt]="logoAlternativeText"
              [ngStyle]="logoStyle"
            />
          </div>
        }
        <header class="document-header" [style.border-color]="accentColor">
          @if (showLogo && !composition.branding.profile) {
            <img
              [src]="seal"
              alt="Synthetic notice seal"
              width="54"
              height="54"
            />
          }
          <button
            type="button"
            class="document-wording"
            aria-label="Edit document header"
            [disabled]="!editable"
            (click)="openDocumentSettings('headerText', $event)"
            [style.color]="accentColor"
          >
            {{ composition.branding.headerText || 'Add header wording' }}
          </button>
        </header>
        <div class="document-body">
          @for (block of composition.blocks; track block.id) {
            @if (block.kind === 'pageBreak') {
              <div
                class="page-break"
                [class.selected]="selected?.id === block.id"
                [attr.data-block-id]="block.id"
                role="separator"
                aria-label="Explicit page break"
              >
                <button
                  type="button"
                  uiButton="secondary"
                  (click)="select(block)"
                  [attr.aria-label]="'Select ' + label(block)"
                  [disabled]="disabled"
                >
                  Page break
                </button>
              </div>
            } @else {
              <section
                class="document-block"
                [attr.data-block-id]="block.id"
                [class.selected]="selected?.id === block.id"
                [ngStyle]="blockStyle(block)"
                (click)="select(block)"
                (keydown.enter)="block.columns && select(block)"
                [attr.tabindex]="block.columns ? 0 : null"
                [attr.aria-label]="label(block)"
              >
                @if (block.runs; as runs) {
                  <app-notice-inline-editor
                    [runs]="runs"
                    [fields]="fields"
                    [terms]="terms"
                    [readOnly]="!editable"
                    [selectedReferenceIndex]="
                      referenceSelection?.blockId === block.id
                        ? referenceSelection!.referenceIndex
                        : null
                    "
                    (referenceSelected)="inspectReference(block, $event)"
                    [ariaLabel]="label(block) + ' text'"
                    (activated)="select(block, false)"
                    (runsChange)="changeRuns(block, $event)"
                    (historyCommand)="historyCommand.emit($event)"
                    (breakHistoryGroup)="breakHistoryGroup.emit()"
                  ></app-notice-inline-editor>
                }
                @if (block.columns; as columns) {
                  <table class="repeat-table">
                    <caption>
                      Repeated data · Final rows in Preview
                    </caption>
                    <thead>
                      <tr>
                        @for (column of columns; track column.id) {
                          <th
                            [style.width.%]="columnWidth(block, column.width)"
                          >
                            {{ column.label }}
                          </th>
                        }
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        @for (column of columns; track column.id) {
                          <td>{{ column.field }}</td>
                        }
                      </tr>
                    </tbody>
                  </table>
                }
              </section>
            }
          }
        </div>
        <footer class="document-footer" [style.border-color]="accentColor">
          <button
            type="button"
            class="document-wording"
            aria-label="Edit document footer"
            [disabled]="!editable"
            (click)="openDocumentSettings('footerText', $event)"
          >
            {{ composition.branding.footerText || 'Add footer wording' }}
          </button>
          <span class="page-count-hint">Page numbers in Preview</span>
        </footer>
      </article>
      <p class="canvas-caption">
        Click wording to edit · References stay protected · Final pagination in
        Preview
      </p>
    </div>
    @if (propertiesOpen) {
      <aside
        #propertiesPane
        class="page-properties"
        (scroll)="rememberViewState()"
        (keydown)="closeProperties($event)"
        [attr.aria-label]="
          inspectorMode === 'document'
            ? 'Document properties'
            : inspectorMode === 'reference'
              ? 'Reference properties'
              : 'Selected element properties'
        "
      >
        <div class="inspector-tabs" aria-label="Properties view">
          <button
            type="button"
            uiButton="secondary"
            [attr.aria-pressed]="inspectorMode === 'element'"
            (click)="showElementProperties()"
          >
            Element
          </button>
          <button
            type="button"
            uiButton="secondary"
            [attr.aria-pressed]="inspectorMode === 'document'"
            (click)="openDocumentSettings('size', $event)"
          >
            Document
          </button>
        </div>
        @if (inspectorMode === 'document') {
          <phms-notice-document-settings
            [assetCatalog]="assetCatalog"
            [assetsLoading]="assetsLoading"
            [assetsError]="assetsError"
            (loadAssets)="loadAssets.emit()"
            [composition]="composition"
            [catalog]="catalog"
            [disabled]="!editable"
            (setting)="documentSetting($event)"
            (breakHistoryGroup)="breakHistoryGroup.emit()"
          />
        } @else if (inspectorMode === 'reference') {
          <phms-notice-reference-properties
            [composition]="composition"
            [catalog]="catalog"
            [selection]="referenceSelection"
            [disabled]="!editable"
            [resolvedText]="resolvedReferenceText"
            [proofLabel]="referenceProofLabel"
            [termEvidence]="referenceTermEvidence"
            [terminologyNode]="referenceTerminologyNode"
            (command)="referenceCommand($event)"
            (breakHistoryGroup)="breakHistoryGroup.emit()"
            (returnToText)="returnToReference()"
          />
        } @else {
          <h3>{{ selected ? label(selected) : 'Select an element' }}</h3>
          <p class="property-hint">Select on the page or in Reading order.</p>
          @if (selected?.kind === 'repeat') {
            <phms-notice-table-properties
              [block]="selected!"
              [catalog]="catalog"
              [contextEnabled]="contextEnabled"
              [disabled]="!editable"
              (command)="structure($event, false)"
            />
          }
          @if (selected?.style; as appearance) {
            <fieldset [disabled]="!editable">
              <legend>Appearance</legend>
              <label
                >Text size (pt)<input
                  uiControl
                  type="number"
                  min="8"
                  max="24"
                  name="page-text-size"
                  [ngModel]="appearance.fontSizePt"
                  (ngModelChange)="style('fontSizePt', $event)"
              /></label>
              <div class="property-pair">
                <label
                  >Text color<input
                    uiControl
                    type="color"
                    name="page-text-color"
                    [ngModel]="appearance.color"
                    (ngModelChange)="style('color', $event)" /></label
                ><label
                  >Fill color<input
                    uiControl
                    type="color"
                    name="page-fill-color"
                    [ngModel]="appearance.backgroundColor"
                    (ngModelChange)="style('backgroundColor', $event)"
                /></label>
              </div>
              <label
                >Border color<input
                  uiControl
                  type="color"
                  name="page-border-color"
                  [ngModel]="appearance.borderColor"
                  (ngModelChange)="style('borderColor', $event)"
              /></label>
              <div class="property-pair">
                <label
                  >Border (pt)<input
                    uiControl
                    type="number"
                    min="0"
                    max="3"
                    step="0.5"
                    name="page-border-width"
                    [ngModel]="appearance.borderWidthPt"
                    (ngModelChange)="style('borderWidthPt', $event)" /></label
                ><label
                  >Padding (pt)<input
                    uiControl
                    type="number"
                    min="0"
                    max="20"
                    name="page-padding"
                    [ngModel]="appearance.paddingPt"
                    (ngModelChange)="style('paddingPt', $event)"
                /></label>
              </div>
              <label class="property-check"
                ><input
                  type="checkbox"
                  name="page-keep-together"
                  [ngModel]="appearance.keepTogether"
                  (ngModelChange)="style('keepTogether', $event)"
                />
                Keep together</label
              >
            </fieldset>
          }
          @if (selected; as block) {
            <fieldset class="block-actions" [disabled]="!editable">
              <legend>Reading order</legend>
              <div class="page-tools">
                <button
                  uiButton="secondary"
                  type="button"
                  (click)="move(-1)"
                  [disabled]="block === composition.blocks[0]"
                >
                  Move up
                </button>
                <button
                  uiButton="secondary"
                  type="button"
                  (click)="move(1)"
                  [disabled]="
                    block === composition.blocks[composition.blocks.length - 1]
                  "
                >
                  Move down
                </button>
                <button
                  uiButton="secondary"
                  type="button"
                  (click)="duplicateBlock()"
                  [disabled]="!canAddBlock || block.kind === 'pageBreak'"
                  title="Duplicate the selected content with a new identity"
                >
                  Duplicate block
                </button>
                <button
                  uiButton="secondary"
                  type="button"
                  (click)="removeBlock()"
                  [disabled]="composition.blocks.length <= 1"
                >
                  Remove block
                </button>
              </div>
            </fieldset>
          }
        }
        @if (selected; as block) {
          @if (references(block).length) {
            <nav
              class="reference-list"
              aria-label="References in selected block"
            >
              <h3>References</h3>
              @for (
                reference of references(block);
                track reference.referenceIndex
              ) {
                <button
                  type="button"
                  uiButton="secondary"
                  [disabled]="!editable"
                  [attr.aria-label]="
                    'Inspect reference ' +
                    (reference.referenceIndex + 1) +
                    ': ' +
                    reference.code
                  "
                  [attr.aria-pressed]="
                    inspectorMode === 'reference' &&
                    referenceSelection?.blockId === reference.blockId &&
                    referenceSelection?.referenceIndex ===
                      reference.referenceIndex
                  "
                  (click)="inspectReference(block, reference)"
                >
                  {{ reference.kind === 'field' ? 'Data' : 'Term' }} ·
                  {{ referenceLabel(reference) }}
                </button>
              }
            </nav>
          }
        }
        <button
          uiButton="secondary"
          type="button"
          (click)="fieldsRequested.emit()"
        >
          Edit all fields
        </button>
      </aside>
    }
  </div>
</section>
